import { ChangeDetectionStrategy, Component, OnInit, Signal, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Service } from '../../../core/models/service.model';
import { LoggerService } from '../../../core/services/logger.service';
import { ServiceService } from '../../../core/services/service.service';
import { TranslationService } from '../../../core/services/translation.service';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardActionsComponent } from '../../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ModalDialogComponent } from '../../../shared/components/modal-dialog/modal-dialog.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

interface ServiceFormModel {
  label: string;
  default_price: number | null;
}

/**
 * Admin CRUD for the org's services-offered list (`config.manage-lists`) —
 * design doc §7. A service with a price gets a quantity/line-total row in a
 * lead's estimate (see interaction-form's lead-capture panel); one with no
 * price is a plain "interested in" toggle.
 */
@Component({
  selector: 'app-services-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    BackButtonComponent,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ModalDialogComponent,
    TranslatePipe,
  ],
  templateUrl: './services-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './services-settings.component.css',
})
export class ServicesSettingsComponent implements OnInit {
  private serviceService = inject(ServiceService);
  private translate = inject(TranslationService);
  private log = inject(LoggerService).getLogger('services-settings');

  services!: Signal<Service[]>;

  showEditor = signal(false);
  editing = signal<Service | null>(null);
  saving = signal(false);
  error = signal<string | null>(null);

  form = new FormGroup({});
  model: ServiceFormModel = { label: '', default_price: null };
  fields: FormlyFieldConfig[] = [];

  ngOnInit(): void {
    this.services = this.serviceService.createQuery().data;

    const t = (key: string) => this.translate.translate(key);
    this.fields = [
      { key: 'label', type: 'text', props: { label: t('settings.services.form.label'), required: true } },
      { key: 'default_price', type: 'number', props: { label: t('settings.services.form.price'), min: 0 } },
    ];
  }

  openCreate(): void {
    this.editing.set(null);
    this.model = { label: '', default_price: null };
    this.error.set(null);
    this.showEditor.set(true);
  }

  openEdit(service: Service): void {
    this.editing.set(service);
    this.model = { label: service.label, default_price: service.default_price };
    this.error.set(null);
    this.showEditor.set(true);
  }

  closeEditor(): void {
    this.showEditor.set(false);
  }

  async save(): Promise<void> {
    this.saving.set(true);
    this.error.set(null);
    try {
      const existing = this.editing();
      if (existing) {
        await this.serviceService.update({ id: existing.id, ...this.model });
      } else {
        await this.serviceService.create(this.model);
      }
      this.showEditor.set(false);
    } catch (err) {
      this.log.error('Failed to save service', err);
      this.error.set(this.translate.translate('settings.services.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  async toggleActive(service: Service): Promise<void> {
    try {
      await this.serviceService.update({ id: service.id, is_active: service.is_active ? 0 : 1 });
    } catch (err) {
      this.log.error('Failed to toggle service active state', err);
    }
  }
}
